import type { ReactElement } from "react"
import { useCallback, useEffect, useState } from "react"
import { useRouter } from "@tanstack/react-router"
import { Loader2 } from "lucide-react"

import { FloorPilotLogo } from "@/components/brand"
import { Button } from "@/components/ui/button"
import { BRAND_NAME } from "@/lib/brand"
import { bumpRetryAttempts, nextAutoRetryDelayMs, readRetryAttempts, resetRetryState } from "@/lib/route-error-utils"

/**
 * Shown while the API is unreachable (deploy window, blip). Auto-retries the failed route load on
 * a backoff schedule, then goes manual-only. Attempt bookkeeping lives at MODULE level in
 * route-error-utils — the error boundary remounts this component after every failed retry
 * (`loadedAt` changes), so component state would restart the backoff from the top forever.
 */
export function ReconnectingScreen(): ReactElement {
	const router = useRouter()
	// Mirrors the module-level record for rendering only.
	const [exhausted, setExhausted] = useState(() => nextAutoRetryDelayMs(readRetryAttempts(window.location.href, Date.now())) === null)

	// invalidate() re-runs beforeLoad (which re-fetches /users/me — a rejected query has no data,
	// so ensureQueryData performs a real refetch). On success the boundary auto-clears; on sign-out
	// mid-retry the promise rejects with a redirect — swallow it. After it settles, check whether
	// any match is still errored: if NOT, the outage is over — clear the attempt record so the next
	// outage starts a fresh backoff instead of resuming mid-schedule (or worse, opening already in
	// the exhausted manual-only state). Unmount alone can't tell recovery from navigation-away.
	const attemptRecovery = useCallback(
		(onStillFailing?: () => void): void => {
			void router
				.invalidate()
				.catch(() => {})
				.finally(() => {
					const stillFailing = router.state.matches.some((m) => m.status === "error")
					if (!stillFailing) {
						resetRetryState()
						return
					}
					onStillFailing?.()
				})
		},
		[router],
	)

	useEffect(() => {
		let cancelled = false
		let timer: number | undefined

		const retry = (): void => {
			bumpRetryAttempts(window.location.href, Date.now())
			attemptRecovery(() => {
				if (!cancelled) schedule()
			})
		}

		const schedule = (): void => {
			const delay = nextAutoRetryDelayMs(readRetryAttempts(window.location.href, Date.now()))
			if (delay === null) {
				setExhausted(true)
				return
			}
			timer = window.setTimeout(retry, delay)
		}

		// Browser says the network came back: skip the pending wait and retry immediately.
		const onOnline = (): void => {
			window.clearTimeout(timer)
			retry()
		}

		schedule()
		window.addEventListener("online", onOnline)
		return () => {
			cancelled = true
			window.clearTimeout(timer)
			window.removeEventListener("online", onOnline)
		}
	}, [router, attemptRecovery])

	return (
		<div className="bg-background flex min-h-svh flex-col items-center justify-center gap-4 px-6 py-16">
			<FloorPilotLogo variant="navbar-wordmark" className="block h-7 w-auto" />
			{!exhausted ? (
				<>
					<div className="text-foreground flex items-center gap-2 text-base font-medium">
						<Loader2 className="size-4 animate-spin" aria-hidden />
						Reconnecting…
					</div>
					<p className="text-muted-foreground max-w-md text-center text-sm">
						{BRAND_NAME} is updating or briefly unreachable. We'll keep trying — no need to refresh.
					</p>
				</>
			) : (
				<>
					<p className="text-foreground text-center text-base font-medium">Still having trouble connecting.</p>
					<p className="text-muted-foreground max-w-md text-center text-sm">
						Check your connection, or try again in a moment.
					</p>
					{/* Manual retry must also reconcile the record — a successful manual recovery that left
					    attempts at the cap would otherwise reopen the NEXT outage in this exhausted state. */}
					<Button type="button" onClick={() => attemptRecovery()}>
						Retry
					</Button>
				</>
			)}
		</div>
	)
}
